import {
  Timeline,
  TimelineAction,
  TimelineRow,
  TimelineState,
} from "@xzdarcy/react-timeline-editor";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useCuepointsStore } from "./useCuepointsStore";
import { Flex, Space, Typography, Button } from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import { usePagePadding } from "components/layout/pagePadding";
import {
  PauseIcon,
  PlayIcon,
  SkipBackIcon,
  SkipForwardIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsInSimpleIcon,
} from "@phosphor-icons/react";
import styled from "styled-components";
import { AiStarIcon } from "components/icons";
import { useHighlightsStore } from "./useHighlightsStore";
const MAX_SCALE = 100;
const MIN_SCALE = 40;
const DEFAULT_SCALE = 60;

type TimelineComponentProps = {
  handleDragStart: () => void;
  handleDragEnd: (time: number) => void;
  handleScroll: (id: string, effect?: boolean) => void;
  duration: number;
  setTs: (time: number) => void;
  ts: number;
  timelineScaleWidth?: number;
};

export const TimelineComponent = forwardRef<
  | {
      container: HTMLDivElement;
      player: TimelineState;
    }
  | TimelineState,
  TimelineComponentProps
>(
  (
    {
      handleDragStart,
      handleDragEnd,
      handleScroll,
      duration,
      setTs,
      ts,
      timelineScaleWidth = DEFAULT_SCALE,
    },
    ref
  ) => {
    const { theme } = useAntTheme();
    // Edge-to-edge timeline: cancels the app shell's horizontal page padding.
    const pagePadding = usePagePadding();
    const [localTimelineScaleWidth, setLocalTimelineScaleWidth] =
      useState(timelineScaleWidth);

    const containerRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<TimelineState>(null);

    useImperativeHandle(ref, () => ({
      container: containerRef.current,
      player: playerRef.current,
      scaleWidth: localTimelineScaleWidth,
    }));

    const [cuepointsTimelineItems, setCuepointsTimelineItems] = useState<
      TimelineRow[]
    >([]);

    const { currentHighlight, highlights, setCurrentHighlight } =
      useHighlightsStore();
    const cuepoints = useCuepointsStore((state) => state.cuepoints);
    const [isPlaying, setIsPlaying] = useState(false);
    const videoElement = document.querySelector("video");

    const handleVideoPlay = () => {
      setIsPlaying(true);
    };

    const handleVideoPause = () => {
      setIsPlaying(false);
    };

    useEffect(() => {
      if (videoElement) {
        videoElement.addEventListener("play", handleVideoPlay);
        videoElement.addEventListener("pause", handleVideoPause);

        return () => {
          videoElement.removeEventListener("play", handleVideoPlay);
          videoElement.removeEventListener("pause", handleVideoPause);
        };
      }
    }, [videoElement]);

    useEffect(() => {
      const actions =
        cuepoints.length === 0
          ? []
          : cuepoints.map((cuepoint) => {
              let property = "highlightId"; // for legacy model HL
              let duration = 1;
              if ("sourceId" in cuepoint) {
                property = "sourceId";
                duration = cuepoint.duration;
              }
              return {
                id: cuepoint[property],
                start: cuepoint.start,
                end: cuepoint.start + duration,
                flexible: true,
                effectId: "effect0",
              };
            });

      const row = [
        {
          id: "0",
          actions,
        },
      ];

      setCuepointsTimelineItems(row);
    }, [cuepoints]);

    const handlePlayPause = () => {
      if (!videoElement) return;
      if (!isPlaying) {
        videoElement.play().catch((error) => {
          console.error("Error playing video", error);
        });
      } else {
        videoElement.pause();
      }
      setIsPlaying((prev) => !prev);
    };

    const fastForward = () => {
      if (videoElement) {
        videoElement.currentTime += 10;
      }
    };

    const rewind = () => {
      if (videoElement) {
        videoElement.currentTime -= 10;
      }
    };

    const setTime = (time: number) => {
      if (videoElement) {
        videoElement.currentTime = time;
        setTs(time * 1000);
      }
    };

    const getScaleTime = (time: number, mills?: boolean) => {
      const minutes = Math.floor(time / 60);
      let seconds = time % 60;
      let milliseconds;

      if (mills) {
        seconds = Math.floor(seconds);
        milliseconds = ((time % 1) * 100).toFixed(0).padStart(2, "0");

        return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}.${milliseconds}`;
      }

      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };

    const effects = {
      effect0: {
        id: "effect0",
        source: {
          enter: (params) => {
            const cuepointId = params.action.id;
            const reachedHighlight = highlights.find(
              (h) => h.id === cuepointId
            );
            // autoscroll to current HL in the timeline and in storyboard
            // when the cuepoint is seeked in the timeline
            // warning : when currentHighlight is undefined (no highlight selected), the enter
            // callback was called and overriding the currentHighlight!
            // we added a check on isPlaying to allow autotrack only when the video is playing
            if (reachedHighlight?.id !== currentHighlight?.id && isPlaying) {
              setCurrentHighlight(reachedHighlight);
              handleScroll(reachedHighlight?.id, true);
            }
          },
        },
      },
    };

    const handleZoomIn = () => {
      setLocalTimelineScaleWidth((prev) => Math.min(prev + 10, MAX_SCALE));
    };

    const handleZoomOut = () => {
      setLocalTimelineScaleWidth((prev) => Math.max(prev - 10, MIN_SCALE));
    };

    const handleZoomReset = () => {
      setLocalTimelineScaleWidth(timelineScaleWidth); // Reset to default value
    };

    return (
      <Flex
        vertical
        style={{
          width: `calc(100% + ${pagePadding.left + pagePadding.right}px)`,
          zIndex: 10,
          borderTop: "1px solid",
          borderColor: theme.colorBorder,
          marginLeft: -pagePadding.left,
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          style={{
            width: "100%",
            height: 40,
            backgroundColor: theme.colorBgBase,
            cursor: "pointer",
          }}
        >
          <div></div>
          {/* fix to center of screen with margin */}
          <Flex gap={20} style={{ marginLeft: 150 }}>
            <Space>
              <SkipBackIcon size={18} onClick={rewind} weight="fill" />
              {isPlaying ? (
                <PauseIcon size={28} onClick={handlePlayPause} weight="fill" />
              ) : (
                <PlayIcon size={28} onClick={handlePlayPause} weight="fill" />
              )}
              <SkipForwardIcon size={18} onClick={fastForward} weight="fill" />
            </Space>
            <Space style={{ width: 150 }} size={"small"}>
              <Typography.Text style={{ color: theme.colorPrimary }}>
                {getScaleTime(Number(ts.toFixed(2)), true)}
              </Typography.Text>
              <Typography.Text type="secondary">/</Typography.Text>
              <Typography.Text>
                {getScaleTime(Number(duration.toFixed(2)), true)}
              </Typography.Text>
            </Space>
          </Flex>
          <Space size={"small"}>
            <Button
              type="text"
              icon={<MagnifyingGlassMinusIcon size={20} />}
              onClick={handleZoomOut}
              disabled={localTimelineScaleWidth <= MIN_SCALE}
            />
            <Button
              type="text"
              icon={<ArrowsInSimpleIcon size={20} />}
              onClick={handleZoomReset}
              disabled={localTimelineScaleWidth === timelineScaleWidth}
            />
            <Button
              type="text"
              icon={<MagnifyingGlassPlusIcon size={20} />}
              onClick={handleZoomIn}
              disabled={localTimelineScaleWidth >= MAX_SCALE}
            />
          </Space>
        </Flex>
        <TimelineWrapper ref={containerRef}>
          <Timeline
            editorData={cuepointsTimelineItems}
            scaleSplitCount={10}
            autoReRender
            autoScroll={true}
            effects={effects}
            onCursorDragStart={handleDragStart}
            onCursorDragEnd={handleDragEnd}
            minScaleCount={duration}
            scaleWidth={localTimelineScaleWidth}
            disableDrag
            getScaleRender={(scale) => getScaleTime(scale)}
            getActionRender={(action, row) => (
              <TimelineCuepoint action={action} row={row} />
            )}
            onClickAction={(e, { action }) => handleScroll(action.id)}
            onClickRow={(e, { time }) => setTime(time)}
            onClickTimeArea={(time) => {
              setTime(time);
              return true;
            }}
            hideCursor={false}
            ref={playerRef}
            style={{ height: 110, width: "100%" }}
          />
        </TimelineWrapper>
      </Flex>
    );
  }
);

const TimelineCuepoint = ({
  action,
}: {
  action: TimelineAction;
  row: TimelineRow;
}) => {
  const { currentHighlight, highlights } = useHighlightsStore();
  const { theme } = useAntTheme();
  const highlightIndex = highlights?.findIndex((h) => {
    return h.id === action.id;
  });

  const generated = highlights[highlightIndex]?.generated;

  if (highlightIndex === -1) return;
  return (
    <Flex
      align="center"
      justify="center"
      className={"effect0"}
      style={{
        backgroundColor:
          action.id === currentHighlight?.id
            ? theme.colorPrimary
            : theme.colorBgSpotlight,
        border: "1px solid",
        borderColor: "rgba(255, 255, 255, 0.6)",
        height: 50,
        borderRadius: theme.borderRadius,
        cursor: "pointer",
        position: "relative",
      }}
    >
      {generated && (
        <div
          style={{
            position: "absolute",
            bottom: -2,
            left: 1,
          }}
        >
          <AiStarIcon
            width={14}
            height={"auto"}
            color={
              action.id === currentHighlight?.id
                ? "#fff"
                : "url(#paint0_linear_282_445)"
            }
            defs={
              action.id === currentHighlight?.id ? (
                ""
              ) : (
                <linearGradient
                  id="paint0_linear_282_445"
                  x1="0%"
                  y1="100%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#EE00C2" />
                  <stop offset="100%" stopColor="#5F00E3" />
                </linearGradient>
              )
            }
          />
        </div>
      )}
      <Typography.Text
        strong
        style={{ color: "#FFFFFF", fontSize: 14, whiteSpace: "nowrap" }}
      >
        {highlightIndex + 1}
      </Typography.Text>
    </Flex>
  );
};

const TimelineWrapper = styled.div`
  .timeline-editor {
    width: 100%;
    height: 120px;
  }

  .timeline-editor-action {
    background-color: transparent;
    z-index: 9999;
    cursor: pointer;
  }

  .timeline-editor-action-left-stretch,
  .timeline-editor-action-right-stretch {
    display: none;
  }

  .timeline-editor-cursor-area {
    background-color: #ffffff;
    width: 2px;
  }

  .timeline-editor-cursor svg path {
    fill: #ffffff;
  }

  .timeline-editor :hover::-webkit-scrollbar:horizontal {
    height: 8px !important;
  }

  .timeline-editor :hover::-webkit-scrollbar-thumb:horizontal {
    background: ${({ theme }) => theme.colorPrimary} !important;
    border-radius: 5px !important;
  }
`;
TimelineComponent.displayName = "TimelineComponent";
